import React from 'react';
import NetworkBoundaryLink from 'app/components/links/NetworkBoundaryLink';
import { Box, Flex, Text, Tooltip } from 'core/components';
import ConnectivityTypeLink from 'app/components/links/ConnectivityTypeLink';
import ProviderLink from 'app/components/links/ProviderLink';
import * as interfaceClassification from '@kentik/ui-shared/dictionary/interfaceClassification';
import Overridden from './OverriddenIconWrapper';

/**
 * This component shows Key/Value pairs representing interface connectivity data including:
 * - Network boundary
 * - Connectivity type
 * - Provider
 */
const InterfaceClassificationKv = ({
  networkBoundary,
  connectivity,
  provider,
  overriddenFields,
  showLinks,
  withTooltip
}) => {
  if (!networkBoundary && !connectivity && !provider) {
    return null;
  }

  // Display values
  const networkBoundaryDisplay = interfaceClassification.networkBoundaryTypes[networkBoundary] || networkBoundary;
  const connectivityDisplay = interfaceClassification.connectivityTypes[connectivity] || connectivity;

  // Overrides
  const networkBoundaryOverridden = overriddenFields?.includes('network_boundary');
  const connectivityOverridden = overriddenFields?.includes('connectivity_type');
  const providerOverridden = overriddenFields?.includes('provider');

  // Tooltip
  const tooltipContent = `${`Boundary: ${networkBoundaryDisplay}${
    networkBoundaryOverridden ? ' (Overridden)' : ''
  }`}, ${`Connectivity: ${connectivityDisplay}${connectivityOverridden ? ' (Overridden)' : ''}`}${
    provider ? `, Provider: ${provider}${providerOverridden ? ' (Overridden)' : ''}` : ''
  }`;

  const Content = (
    <Box overflow="hidden" tabIndex={0}>
      <Flex alignItems="center">
        <Text ellipsis>
          <Text small muted>
            Boundary:{' '}
          </Text>
          {showLinks ? <NetworkBoundaryLink value={networkBoundary} /> : networkBoundaryDisplay}
        </Text>
        <Overridden isOverridden={networkBoundaryOverridden} />
      </Flex>
      <Flex alignItems="center">
        <Text ellipsis>
          <Text small muted>
            Connectivity:{' '}
          </Text>
          {showLinks ? <ConnectivityTypeLink value={connectivity} /> : connectivityDisplay}
        </Text>
        <Overridden isOverridden={connectivityOverridden} />
      </Flex>
      {provider && (
        <Flex alignItems="center">
          <>
            <Text ellipsis>
              <Text small muted>
                Provider:{' '}
              </Text>
              {showLinks ? <ProviderLink provider={provider} /> : provider}
            </Text>
            <Overridden isOverridden={providerOverridden} />
          </>
        </Flex>
      )}
    </Box>
  );

  return withTooltip ? (
    <Tooltip fill content={tooltipContent}>
      {Content}
    </Tooltip>
  ) : (
    Content
  );
};

export default InterfaceClassificationKv;
